import { WorkoutWithRelations } from '@/utils/types';
import { endOfDay, startOfDay } from 'date-fns';
import { create } from 'zustand';
import { createMapArrayCache } from './mapArrayCache';

export interface WorkoutState {
  workouts: Map<string, WorkoutWithRelations>;
  lastSyncAt: number;
  mergeWorkouts: (newWorkouts: WorkoutWithRelations[]) => void;
  removeWorkout: (workoutId: string) => void;
  removeWorkoutsByDateAndSport: (date: Date, sportType: string) => void;
  removeWorkoutsByDateRange: (range: { start: Date; end: Date }) => void;
  clearWorkouts: () => void;
  touchWorkouts: () => void;
  getFilteredWorkouts: (range?: {
    start: Date;
    end: Date;
  }) => WorkoutWithRelations[];
}

const getWorkoutsArray = createMapArrayCache<string, WorkoutWithRelations>();

export const useWorkoutStore = create<WorkoutState>((set, get) => ({
  workouts: new Map(),
  lastSyncAt: 0,

  mergeWorkouts: (newWorkouts) => {
    set((state) => {
      const updatedMap = new Map(state.workouts);
      newWorkouts.forEach((workout) => {
        updatedMap.set(workout.id.toString(), workout);
      });
      return { workouts: updatedMap };
    });
  },

  removeWorkout: (workoutId) => {
    set((state) => {
      const updatedMap = new Map(state.workouts);
      updatedMap.delete(workoutId);
      return { workouts: updatedMap };
    });
  },

  removeWorkoutsByDateAndSport: (date, sportType) => {
    set((state) => {
      const updatedMap = new Map(state.workouts);
      const targetDate = startOfDay(date);

      for (const [id, workout] of updatedMap.entries()) {
        if (workout.sportType === sportType && workout.date) {
          const workoutDate =
            typeof workout.date === 'string'
              ? new Date(workout.date)
              : workout.date;
          if (startOfDay(workoutDate).getTime() === targetDate.getTime()) {
            updatedMap.delete(id);
          }
        }
      }

      return { workouts: updatedMap };
    });
  },

  removeWorkoutsByDateRange: (range) => {
    set((state) => {
      const updatedMap = new Map(state.workouts);
      const rangeStart = startOfDay(range.start);
      const rangeEnd = endOfDay(range.end);

      for (const [id, workout] of updatedMap.entries()) {
        if (workout.date) {
          const workoutDate =
            typeof workout.date === 'string'
              ? new Date(workout.date)
              : workout.date;
          const workoutDay = startOfDay(workoutDate);
          if (workoutDay >= rangeStart && workoutDay <= rangeEnd) {
            updatedMap.delete(id);
          }
        }
      }

      return { workouts: updatedMap };
    });
  },

  clearWorkouts: () => {
    set({ workouts: new Map(), lastSyncAt: Date.now() });
  },

  touchWorkouts: () => {
    set({ lastSyncAt: Date.now() });
  },

  getFilteredWorkouts: (range) => {
    const workouts = getWorkoutsArray(get().workouts);

    if (!range) {
      return workouts;
    }

    const rangeStart = startOfDay(range.start);
    const rangeEnd = endOfDay(range.end);

    return workouts.filter((workout) => {
      if (!workout.date) return false;
      const workoutDate =
        typeof workout.date === 'string'
          ? new Date(workout.date)
          : workout.date;
      return workoutDate >= rangeStart && workoutDate <= rangeEnd;
    });
  },
}));
